<template>
  <TransitionRoot appear :show="!modalHidden" as="template">
    <Dialog as="div" @close="closeModal" @keypress="keypress">
      <div class="fixed inset-0 z-10 flex items-center justify-center px-1 sm:px-4">
        <TransitionChild
          as="template"
          enter="duration-100 ease-out"
          enter-from="opacity-0"
          enter-to="opacity-50"
          leave="duration-100 ease-in"
          leave-from="opacity-50"
          leave-to="opacity-0"
        >
          <DialogOverlay class="fixed inset-0 bg-gray-500 bg-opacity-50" />
        </TransitionChild>

        <TransitionChild
          as="template"
          enter="duration-100 ease-out"
          enter-from="opacity-0 scale-95"
          enter-to="opacity-100 scale-100"
          leave="duration-100 ease-in"
          leave-from="opacity-100 scale-100"
          leave-to="opacity-0 scale-95"
        >
          <DialogPanel
            class="relative z-10 flex flex-col px-8 md:px-12 py-12 max-h-[90vh] text-left transition-all transform bg-white shadow-xl rounded-2xl"
            :class="isFullWidth ? ['w-full'] : ['w-full lg:w-2/3 xl:w-1/2']"
          >
            <div class="flex justify-between shrink-0">
              <DialogTitle
                v-if="title"
                as="h3"
                class="text-lg font-medium leading-6 text-gray-900 align-baseline"
              >
                {{ title }}
              </DialogTitle>
              <div class="ml-6">
                <BaseButton @click="closeModal" iconOnly>
                  <i-heroicons-outline-x />
                </BaseButton>
              </div>
            </div>
            <div ref="scrollContainerRef" class="mt-2 overflow-y-auto overscroll-contain ios-scroll">
              <slot></slot>
            </div>
          </DialogPanel>
        </TransitionChild>
      </div>
    </Dialog>
  </TransitionRoot>
</template>

<style scoped>
.ios-scroll {
  -webkit-overflow-scrolling: touch;
}
/* Prevent images from capturing touch gestures (drag-to-save) on iOS,
   which interferes with scroll behavior inside the popup */
.ios-scroll :deep(img) {
  -webkit-touch-callout: none;
  touch-action: pan-y;
  pointer-events: none;
  user-select: none;
  -webkit-user-select: none;
  -webkit-user-drag: none;
}
</style>

<script setup lang="ts">
import {
  TransitionRoot,
  TransitionChild,
  Dialog,
  DialogOverlay,
  DialogPanel,
  DialogTitle,
} from "@headlessui/vue";
import { computed, ref, watch } from "vue";

const scrollContainerRef = ref<HTMLElement | null>(null);
const modalHidden = computed(() => !props.isVisible);

// Headless UI's iOS scroll lock adds a document-level touchmove handler that
// calls preventDefault(), blocking all scrolling inside the Dialog — even on
// elements that should scroll. We counter this by adding our own touchmove
// handler on the scroll container that re-enables scrolling via stopPropagation,
// preventing the event from reaching Headless UI's document-level handler.
// We listen on the scroll container AND use { capture: true } so we intercept
// touchmove from all descendants (including <img>) before Headless UI can.
watch(scrollContainerRef, (el) => {
  if (!el) return;
  el.addEventListener(
    "touchmove",
    (e) => {
      if (el.scrollHeight > el.clientHeight) {
        e.stopPropagation();
      }
    },
    { passive: false, capture: true }
  );
});

//Props with defaults
const props = withDefaults(
  defineProps<{
    isVisible?: boolean;
    title?: string;
    isFullWidth?: boolean;
  }>(),
  {
    isVisible: false,
    title: "",
    isFullWidth: false,
  }
);

const emit = defineEmits<{
  (e: "close"): void;
}>();

let closeModal = () => emit("close");

function keypress(e: KeyboardEvent) {
  console.log("Popup saw keypress", e);
  if (e.key === "Escape") {
    closeModal();
  }
}
</script>
